import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe } from '@ngx-translate/core';
import { JjamesButtonComponent } from 'shared-lib';
import { ParkingSpaceService } from '../../../api/gen';
import { ParkingSpaceDto } from '../../../api/gen/model/parkingSpaceDto';

export interface QuickEasypassSettingsDialogData {
    parkingSpace: ParkingSpaceDto;
}

/**
 * Edits the Quick Easypass block of one space. Always sends the whole block (the endpoint replaces it,
 * like the late-payment one) and passes the time zone through untouched: it is not picked here.
 */
@Component({
    selector: 'app-quick-easypass-settings-dialog',
    standalone: true,
    imports: [FormsModule, MatDialogModule, MatIconModule, JjamesButtonComponent, TranslatePipe],
    templateUrl: './quick-easypass-settings-dialog.component.html'
})
export class QuickEasypassSettingsDialogComponent {
    private dialogRef = inject(MatDialogRef<QuickEasypassSettingsDialogComponent>);
    private parkingSpaceService = inject(ParkingSpaceService);
    private snackBar = inject(MatSnackBar);
    data = inject<QuickEasypassSettingsDialogData>(MAT_DIALOG_DATA);

    enabled = signal(this.data.parkingSpace.quickEasypassEnabled ?? false);
    // The API sends "HH:mm:ss"; <input type="time"> works in "HH:mm".
    cutoff = signal((this.data.parkingSpace.quickEasypassCutoffLocalTime ?? '00:00:00').slice(0, 5));
    chargeAfterCutoff = signal(this.data.parkingSpace.quickEasypassChargeAfterCutoff ?? true);
    isSaving = signal(false);

    hasOrganization = !!this.data.parkingSpace.externalOrganizationId;

    // The server refuses to enable a space with no organization to bill; say so before the round trip.
    canSave = computed(() => !this.isSaving() && !!this.cutoff() && (!this.enabled() || this.hasOrganization));

    onCancel(): void {
        this.dialogRef.close(false);
    }

    onSave(): void {
        if (!this.canSave()) return;

        this.isSaving.set(true);
        this.parkingSpaceService
            .parkingSpaceParkingSpaceIdQuickEasypassSettingsPut(this.data.parkingSpace.id!, {
                enabled: this.enabled(),
                cutoffLocalTime: `${this.cutoff()}:00`,
                chargeAfterCutoff: this.chargeAfterCutoff(),
                timeZoneId: this.data.parkingSpace.quickEasypassTimeZoneId ?? 'Europe/Vienna'
            })
            .subscribe({
                next: () => {
                    this.isSaving.set(false);
                    this.dialogRef.close(true);
                },
                error: (err) => {
                    this.isSaving.set(false);
                    console.error('Failed to update Quick Easypass settings', err);
                    this.snackBar.open(err?.error?.detail ?? 'Could not save Quick Easypass settings.', 'Dismiss', { duration: 5000 });
                }
            });
    }
}
